import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
    Sparkles, Loader2, ChevronRight, Zap, Shield, AlertTriangle, Crown, Compass,
    CalendarClock, Activity, BarChart3,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { ESTRATEGIA_LABELS, ESTRATEGIA_BADGES } from './EstrategiaPortafolioSelector';
import WelcomeVentanaBanner from './WelcomeVentanaBanner';

// Mapeo corto ("1.7") → ID largo del frontend (para deep-link a Chat / Borrador)
// Rúbricas 2026: solo se exponen los IDs cortos vigentes.
const CORTO_A_LARGO = {
    '1.1': 'indicador-1-1',
    '1.2': 'indicador-1-2',
    '1.3': 'indicador-1-3',
    '1.4': 'indicador-2-1',
    '1.5': 'indicador-2-2',
    '1.6': 'indicador-2-3',
    '1.7': 'indicador-3-1',
    '2.9': 'indicador-m2-9',
    '2.10': 'indicador-m2-10',
    '2.11': 'indicador-m2-11',
    '2.12': 'indicador-m2-12',
    '2.13': 'indicador-m2-13',
    '2.14': 'indicador-m2-14',
    '2.15': 'indicador-m2-15',
    '3.16': 'indicador-m3-16',
    '3.18': 'indicador-m3-18',
    '3.20': 'indicador-m3-21',
    '3.22': 'indicador-m3-23',
};

const TIPO_TONO = {
    'Tractor':       { Icon: Zap,            wrap: 'border-emerald-200 bg-emerald-50/60', chip: 'bg-emerald-100 text-emerald-900', dot: 'bg-emerald-500', titulo: 'Tractor pedagógico',  hint: 'Arrastra otros indicadores cuando lo trabajas con profundidad.' },
    'Mantención':    { Icon: Shield,         wrap: 'border-slate-200 bg-slate-50/60',     chip: 'bg-slate-200 text-slate-800',    dot: 'bg-slate-400',   titulo: 'Mantención',          hint: 'Asegura Competente sólido sin sobreinvertir tiempo.' },
    'Alto Desgaste': { Icon: AlertTriangle,  wrap: 'border-rose-200 bg-rose-50/60',       chip: 'bg-rose-100 text-rose-900',      dot: 'bg-rose-500',    titulo: 'Alto desgaste',       hint: 'Cuida tu energía: prioriza coherencia sobre profundidad.' },
    'Focalizado':    { Icon: Crown,          wrap: 'border-violet-200 bg-violet-50/60',   chip: 'bg-violet-100 text-violet-900',  dot: 'bg-violet-500',   titulo: 'Foco estratégico',    hint: 'Buen candidato realista a Destacado con evidencia rigurosa.' },
};

function priorizarIndicadores(rec) {
    // Devuelve hasta 2 indicadores priorizados por la lógica del motor.
    // Política: 1) primero Destacado_candidatos (mayor impacto + factibles), 2) luego asegurar_competente.
    // En estrategias sin destacado_candidatos, mostramos los 2 primeros asegurar_competente (foco protector).
    const out = [];
    if (rec?.destacado_candidatos?.length) {
        out.push(...rec.destacado_candidatos.slice(0, 2));
    }
    if (out.length < 2 && rec?.asegurar_competente?.length) {
        out.push(...rec.asegurar_competente.slice(0, 2 - out.length));
    }
    return out.slice(0, 2);
}

export default function EstaSemanaCard() {
    const navigate = useNavigate();
    const { profile } = useAuth();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    const estrategia = profile?.perfil?.estrategia_portafolio || null;

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await api.get('/strategy/recommendations');
                if (alive) setData(r.data);
            } catch (_) {
                if (alive) setData(null);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [estrategia]);

    // Sin estrategia elegida: invitación sobria a definirla
    if (!loading && !estrategia) {
        return (
            <div className="space-y-4">
                <WelcomeVentanaBanner temporal={data?.temporal} />
                <Card className="border-amber-200 bg-gradient-to-br from-amber-50/70 to-rose-50/30" data-testid="esta-semana-card">
                    <CardContent className="flex items-start gap-4 p-5">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-100">
                            <Sparkles className="h-5 w-5 text-amber-700" />
                        </div>
                        <div className="flex-1 space-y-1.5">
                            <p className="font-heading text-sm font-bold text-slate-900">Esta semana enfócate en…</p>
                            <p className="text-sm text-slate-700 leading-relaxed">
                                Elige tu Estrategia de Portafolio para que el mentor inteligente te sugiera 1 a 2 indicadores prioritarios cada semana,
                                ajustados a tu realidad docente (tiempo, energía y fortalezas).
                            </p>
                        </div>
                        <Button
                            size="sm"
                            onClick={() => navigate('/progreso')}
                            className="shrink-0 gap-1.5"
                            data-testid="esta-semana-elegir-estrategia"
                        >
                            Elegir estrategia
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (loading) {
        return (
            <Card className="border-slate-200" data-testid="esta-semana-card">
                <CardContent className="flex items-center gap-3 p-5 text-sm text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Preparando tu enfoque de esta semana…
                </CardContent>
            </Card>
        );
    }

    const rec = data?.recomendaciones || {};
    const sugeridos = priorizarIndicadores(rec);
    const temporal = data?.temporal || null;

    if (sugeridos.length === 0) {
        return <WelcomeVentanaBanner temporal={temporal} />;
    }

    const etiquetaEstrategia = ESTRATEGIA_LABELS[estrategia] || 'tu estrategia';
    const esAltoRendimiento = estrategia === 'alto_rendimiento';

    const faseEtiqueta = temporal?.fase?.etiqueta;
    const semanaActual = temporal?.semana_actual;
    const totalSemanas = temporal?.total_semanas;
    const semanasRest = temporal?.semanas_restantes;

    return (
        <div className="space-y-4">
            <WelcomeVentanaBanner temporal={temporal} />
            <Card className="border-slate-200 bg-gradient-to-br from-white via-white to-slate-50/60 shadow-sm" data-testid="esta-semana-card">
            <CardContent className="p-5 sm:p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-3">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-900">
                            <Compass className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="font-heading text-base font-bold text-slate-900">Esta semana enfócate en…</p>
                            <p className="mt-0.5 text-xs text-slate-500 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                                <span>Recomendación del Motor Estratégico · {etiquetaEstrategia}</span>
                                {ESTRATEGIA_BADGES[estrategia] && (
                                    <span
                                        className="inline-flex items-center rounded-md bg-slate-900 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white"
                                        data-testid="esta-semana-badge-acontista"
                                    >
                                        {ESTRATEGIA_BADGES[estrategia]}
                                    </span>
                                )}
                            </p>
                            {faseEtiqueta && (
                                <p
                                    className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-slate-500"
                                    data-testid="esta-semana-temporal-fase"
                                >
                                    <CalendarClock className="h-3 w-3" />
                                    {semanaActual ? `Semana ${semanaActual} de ${totalSemanas}` : 'Fase'}
                                    <span className="text-slate-300">·</span>
                                    {faseEtiqueta}
                                    {semanasRest > 0 && semanaActual && (
                                        <>
                                            <span className="text-slate-300">·</span>
                                            <span>{semanasRest} sem restantes</span>
                                        </>
                                    )}
                                </p>
                            )}
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/progreso')}
                        className="text-xs text-slate-500 hover:text-slate-900"
                        data-testid="esta-semana-cambiar-estrategia"
                    >
                        Cambiar estrategia
                    </Button>
                </div>

                {temporal?.mensaje_inicio && temporal.categoria_inicio && temporal.categoria_inicio !== 'oportuno' && temporal.categoria_inicio !== 'previo' && (
                    <div
                        className="mt-4 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 leading-relaxed"
                        data-testid="esta-semana-temporal-mensaje-inicio"
                    >
                        <CalendarClock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />
                        <span>{temporal.mensaje_inicio}</span>
                    </div>
                )}

                {temporal?.recalibracion && (
                    <div
                        className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-900 leading-relaxed"
                        data-testid="esta-semana-temporal-recalibracion"
                    >
                        <Activity className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                        <span>{temporal.recalibracion.mensaje}</span>
                    </div>
                )}

                {temporal?.desequilibrio && (
                    <div
                        className="mt-3 flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 leading-relaxed"
                        data-testid="esta-semana-temporal-desequilibrio"
                    >
                        <BarChart3 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />
                        <span>{temporal.desequilibrio.mensaje}</span>
                    </div>
                )}

                {temporal?.ritmo && ['atencion', 'reducido'].includes(temporal.ritmo.nivel) && (
                    <div
                        className="mt-3 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2 text-[11px] text-slate-700 leading-relaxed"
                        data-testid="esta-semana-temporal-ritmo"
                    >
                        <Activity className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />
                        <span>{temporal.ritmo.mensaje}</span>
                    </div>
                )}

                {esAltoRendimiento && (
                    <div
                        className="mt-4 flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50/70 px-3 py-2 text-xs text-orange-900 leading-relaxed"
                        data-testid="esta-semana-advertencia-alto-rendimiento"
                    >
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-orange-600" />
                        <span>
                            Estrategia de alta exigencia. Si esta semana sientes sobrecarga, te sugerimos pasar a <strong>Mixta</strong> o <strong>Ruta Inteligente</strong> sin culpa.
                        </span>
                    </div>
                )}

                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    {sugeridos.map((ind) => {
                        const tono = TIPO_TONO[ind.tipo] || TIPO_TONO['Focalizado'];
                        const Icon = tono.Icon;
                        const idLargo = CORTO_A_LARGO[ind.id];
                        return (
                            <div
                                key={ind.id}
                                className={cn('flex flex-col gap-3 rounded-xl border p-4', tono.wrap)}
                                data-testid={`esta-semana-indicador-${ind.id}`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                        <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold', tono.chip)}>
                                            <Icon className="h-3 w-3" />
                                            {tono.titulo}
                                        </span>
                                    </div>
                                    <span className="text-[11px] font-mono text-slate-500">#{ind.id}</span>
                                </div>
                                <div>
                                    <p className="font-heading text-sm font-semibold text-slate-900 leading-snug">{ind.nombre}</p>
                                    <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{ind.estrategia_corta}</p>
                                </div>
                                <p className="text-[11px] text-slate-500 leading-relaxed italic">{tono.hint}</p>
                                <div className="flex gap-2 mt-1">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="flex-1 text-xs h-8"
                                        onClick={() => navigate('/chat', { state: { indicador_id: idLargo } })}
                                        data-testid={`esta-semana-cta-chat-${ind.id}`}
                                    >
                                        Consultar IA
                                    </Button>
                                    <Button
                                        size="sm"
                                        className="flex-1 text-xs h-8 gap-1"
                                        onClick={() => navigate('/borradores', { state: { indicador_id: idLargo } })}
                                        data-testid={`esta-semana-cta-borrador-${ind.id}`}
                                    >
                                        Borrador
                                        <ChevronRight className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
        </div>
    );
}
